import { Map } from 'immutable'
import { AsyncState, loadingAsyncState } from 'widgets-for-react'
import { ImageSize } from '../../cms/blocks/types.gen'
import { getTags, PaginatedItemStone } from '../../drupal/api.mod'
import { getDrupalLanguage } from '../../drupal/types_and_parser/extra_types.mod'
import { getPage } from '../drupalPage/api'
import { Page } from '../drupalPage/state'
import { OverviewPageParams } from '../route/state'
import { areArraysDifferent } from '../utils/areArraysDifferent'
import { getLangFromUrl } from '../utils/history.mod'
import { getOverviewItems } from './api'
import { decodeTag, PageKind, pageKindToTagList, tagToTaxonomy, tagUrlParameter } from './utils'

export type OverviewState = {
  overviewPage: AsyncState<Page>
  overviewItems: AsyncState<PaginatedItemStone<ParsedOverviewDetailItem>>
  tags: Map<string, AsyncState<Array<string>>>
  selectedOptions: { tags: Tags; searchQuery: string; currentPage: number }
  searchValue: string
  pageKind: PageKind
  showFilters: boolean
}

export type ParsedOverviewDetailItem = {
  Title: string
  Tags: string[]
  Description: string
  Link: { url: string; title: string }
  Image: Map<ImageSize, string>
  ImageAltText?: string
  get_default_data_src: () => ImageSize
  get_default_data_srcset: () => [ImageSize, string][]
  Author?: string
  ReadingTime?: number
  CreationDate?: string
  meta?: string[]
}

export type Tags = Map<string, Array<string>>

export const initialOverviewState = (
  overviewPageParams: OverviewPageParams,
  pageKind: PageKind,
  previousState?: OverviewState
): OverviewState => {
  const tagList = pageKindToTagList(pageKind)

  let selectedTags = Map<string, Array<string>>()
  tagList.forEach((tag) => {
    selectedTags = selectedTags.set(
      tag,
      overviewPageParams[tagUrlParameter(tag)] ? overviewPageParams[tagUrlParameter(tag)].map((r) => decodeTag(r)) : []
    )
  })

  const overviewPage =
    previousState == undefined || previousState.pageKind != pageKind
      ? loadingAsyncState(() => getPage(overviewPageParams.slug))
      : previousState.overviewPage

  let tags = Map<string, AsyncState<Array<string>>>()
  tagList.forEach((tag) => {
    const previous = previousState == undefined ? undefined : previousState.tags.get(tag)
    tags = tags.set(
      tag,
      loadingAsyncState(
        () => getTags(getDrupalLanguage(getLangFromUrl()), tagToTaxonomy(tag), selectedTags),
        previous && previous.kind == 'loaded' ? previous.value : undefined
      )
    )
  })

  const selectedOptions = {
    tags: selectedTags,
    currentPage: overviewPageParams.page || 0,
    searchQuery: overviewPageParams.searchQuery || ''
  }

  const overviewItems =
    previousState == undefined ||
    previousState.selectedOptions.searchQuery != selectedOptions.searchQuery ||
    previousState.selectedOptions.currentPage != selectedOptions.currentPage ||
    tagList.some((tag) =>
      areArraysDifferent(previousState.selectedOptions.tags.get(tag) || [], selectedTags.get(tag) || [])
    )
      ? loadingAsyncState(() =>
          getOverviewItems(
            pageKind,
            selectedTags,
            selectedOptions.searchQuery,
            selectedOptions.currentPage,
            10,
            getDrupalLanguage(getLangFromUrl())
          )
        )
      : previousState.overviewItems

  return {
    tags,
    pageKind,
    selectedOptions,
    overviewPage,
    overviewItems,
    showFilters: previousState == undefined ? false : previousState.showFilters,
    searchValue: selectedOptions.searchQuery || ''
  }
}
